/**
 * Composer — replica app.jsx:234-243.
 *
 * Textarea autosize (max 120px) + send button 44×44 brand.
 * Enter envía (Shift+Enter newline). Disabled cuando sseState busy o input vacío.
 *
 * Hotfix UAT 2026-05-08 (Issue A — profile gate frontend safety): cuando
 * `disabled=true` (ej: profile=null), el composer queda inhabilitado con
 * placeholder y send button en estado disabled, manteniendo tap target ≥44px.
 * Defense-in-depth con backend GUARD-03 (que también rechaza queries sin
 * profile). Drimian P7 — el gate UX evita que la farmacéutica envíe queries
 * sin perfil y reciba respuestas inseguras (ej: loperamida para "bebé 15
 * meses con diarrea" cuando profile defaultea silenciosamente a adulto).
 *
 * UI-SPEC línea 270: placeholder "Escribe síntoma, marca o principio activo…".
 */
import { useEffect, useRef } from 'react'
import { Icon } from './icons'
import { Dictado } from './Dictado'
import { Escaner } from './Escaner'
import type { SseState } from '../store/sseReducer'
import type { ProfileType } from '../types/profile'

interface Props {
  value: string
  onChange: (v: string) => void
  onSend: () => void
  sseState: SseState
  /** UX feedback Elvio 2026-05-08 — badge "para quién" arriba del composer
   * + tinte de borde por perfil. Da contexto persistente al escribir. */
  profile?: ProfileType | null
  /** Hotfix UAT 2026-05-08 — bloqueo externo (typically: profile=null). */
  disabled?: boolean
  /** Placeholder/aria override cuando disabled=true. */
  disabledReason?: string
  /** 21-oct: código de barras leído con la cámara (se consulta directo). */
  onCodigo?: (codigo: string) => void
}

const PROFILE_LABELS: Record<ProfileType, string> = {
  adult: 'Adulto',
  child_3y: 'Niño 0-4 años',
  child_5y: 'Niño 5-7 años',
  child_8y: 'Niño 8-11 años',
  child_12y: 'Niño 12+ años',
  pregnancy: 'Embarazo',
  lactation: 'Lactancia',
}

// 2026-05-12 — UX Elvio: el badge "Para [perfil]" y el border del textarea
// toman los tokens OKLCH del root, que ya están tinteados por
// applyProfileTheme(profile) en App.tsx según el perfil seleccionado.
// Sin colores hardcoded — todo desde `--color-brand-*` para consistencia
// visual y dark-mode-ready a futuro.
const PROFILE_COLOR_VARS = {
  border: 'var(--color-brand)',
  bg: 'var(--color-brand-soft)',
  ink: 'var(--color-brand-ink)',
}

export function Composer({
  value,
  onChange,
  onSend,
  sseState,
  profile = null,
  disabled = false,
  disabledReason,
  onCodigo,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Autosize basado en scrollHeight, max 120px (UI-SPEC).
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(120, el.scrollHeight)}px`
  }, [value])

  const isBusy =
    sseState.status === 'classifying' ||
    sseState.status === 'searching' ||
    sseState.status === 'thinking' ||
    sseState.status === 'middleware_block'

  const canSend = value.trim().length > 0 && !isBusy && !disabled

  const placeholder = disabled
    ? disabledReason || 'Selecciona el perfil del paciente arriba primero'
    : 'Escribí síntoma, marca, principio activo o receta…'

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (canSend) onSend()
    }
  }

  const profileColor = profile ? PROFILE_COLOR_VARS : null
  const profileLabel = profile ? PROFILE_LABELS[profile] : null

  return (
    <div
      className="border-t"
      style={{
        background: 'var(--color-surface)',
        borderColor: 'var(--color-border)',
      }}
    >
      {profileLabel && profileColor && (
        <div
          className="px-4 pt-2 pb-1 flex items-center gap-2"
          style={{ background: 'var(--color-surface)' }}
        >
          <span
            className="text-[13px] font-medium leading-none"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Para
          </span>
          <span
            className="text-[12px] font-semibold leading-none"
            style={{
              padding: '5px 10px',
              borderRadius: 999,
              background: profileColor.bg,
              color: profileColor.ink,
              border: `1px solid ${profileColor.border}`,
            }}
          >
            {profileLabel}
          </span>
        </div>
      )}

      <div className="flex items-end gap-2 px-4 py-3">
        <div
          className="composer-caja flex-1 transition-colors"
          style={{
            padding: '10px 14px',
            borderRadius: 22,
            border: `2px solid ${profileColor ? profileColor.border : 'var(--color-border-strong)'}`,
            background: disabled
              ? 'var(--color-surface-muted, #f3f4f6)'
              : profileColor
                ? profileColor.bg
                : 'var(--color-bg)',
            opacity: disabled ? 0.7 : 1,
          }}
        >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Mensaje al asistente"
          aria-disabled={disabled}
          disabled={disabled}
          rows={1}
          className="composer-texto block w-full resize-none bg-transparent outline-none text-[15px]"
          style={{
            color: 'var(--color-text)',
            fontFamily: 'var(--font-sans)',
            lineHeight: 1.4,
            maxHeight: 120,
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
      </div>

      {onCodigo && <Escaner onCodigo={onCodigo} disabled={disabled || isBusy} />}
      <Dictado value={value} onChange={onChange} disabled={disabled || isBusy} />
      <button
        type="button"
        aria-label="Enviar mensaje"
        onClick={onSend}
        disabled={!canSend}
        className="grid place-items-center transition-opacity"
        style={{
          minHeight: 44,
          minWidth: 44,
          width: 44,
          height: 44,
          borderRadius: 999,
          background: 'var(--color-brand)',
          color: 'white',
          border: 'none',
          cursor: canSend ? 'pointer' : 'not-allowed',
          opacity: canSend ? 1 : 0.45,
        }}
      >
        <Icon.Send />
      </button>
      </div>
    </div>
  )
}
